import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "flex items-center justify-between py-3.5 px-8 bg-primary-color-600"
}
const _hoisted_2 = { class: "flex items-center gap-x-3" }
const _hoisted_3 = { class: "text-white font-medium" }
const _hoisted_4 = { class: "flex gap-x-4" }
const _hoisted_5 = { class: "w-[330px] absolute z-50 -left-48 rounded-md shadow-lg bg-white p-4 ring-1 ring-black ring-opacity-5" }
const _hoisted_6 = /*#__PURE__*/_createElementVNode("p", { class: "text-sm text-gray-900 mb-1" }, "Current Due Date", -1)
const _hoisted_7 = { class: "text-sm text-gray-500 mb-4" }
const _hoisted_8 = /*#__PURE__*/_createElementVNode("p", { class: "text-sm text-gray-700 mb-1" }, "New Due Date", -1)
const _hoisted_9 = { class: "flex justify-between mb-4" }
const _hoisted_10 = { class: "flex gap-4" }
const _hoisted_11 = { class: "flex flex-col items-center justify-center" }
const _hoisted_12 = /*#__PURE__*/_createElementVNode("p", { class: "text-sm text-white" }, "Progress", -1)
const _hoisted_13 = { class: "text-sm font-semibold text-white" }

import { computed, onMounted, ref, watch } from "vue"
import GgmsButton from "@/components/GgmsButton.vue"
import GgmsInput from "@/components/GgmsInput.vue"
import GgmsGeneralAgent from "@/components/GgmsGeneralAgent.vue"
import { CalendarIcon, RewindIcon, FastForwardIcon, CheckCircleIcon, XIcon, ClockIcon } from "@heroicons/vue/outline"
import { useTaskStore } from "@/stores/task"
import { Task } from "@/shared/models/task"
import { getInitials, formatHumanReadableDate, getDateInputFormat, getTimeInputFormat } from "@/shared/utils/helpers"
import { ToastService } from "@/shared/services/toast"


export default /*#__PURE__*/_defineComponent({
  __name: 'GgmsTaskSessionBar',
  setup(__props) {

const taskStore = useTaskStore()
const toastService = new ToastService()

const activeTasks = computed(() => taskStore.activeTasks)
const activeTask = computed(() => taskStore.activeTask)

const activeTaskIndex = computed(() => activeTasks.value.findIndex((task) => task._id === activeTask.value?._id))
const fullNameInitiales = computed(() => getInitials(activeTask?.value?.agent?.fullName || "G G"))
const isPreviousActive = computed(() => getPreviousPendingTask() !== -1)
const isNextActive = computed(() => getNextPendingTask() !== -1)

const activeTaskDate = computed(() => formatHumanReadableDate(new Date(activeTask.value.when?.startAt || ""), false))
const newTaskDate = ref(getDateInputFormat(new Date()))
const newTaskTime = ref(getTimeInputFormat(new Date()))

const rescheduleOverlayPanel = ref()

function toggleReschedulePanel(event?: Event) {
    rescheduleOverlayPanel.value.toggle(event)
}

function setTaskStartDate(): string {
    const date = new Date(newTaskDate.value)
    const hours = Number(newTaskTime.value.split(":")[0])
    const minutes = Number(newTaskTime.value.split(":")[1])
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), hours, minutes).toISOString()
}

async function rescheduleTask() {
    const updatedActiveTask = { ...activeTask.value }
    updatedActiveTask.when.startAt = setTaskStartDate()
    delete updatedActiveTask.agent
    updatedActiveTask.agentId = activeTask.value.agent?._id

    await taskStore.updateTask(activeTask.value._id || "", updatedActiveTask)

    toggleReschedulePanel()
    toastService.addToast({
        type: "success",
        message: "Task rescheduled.",
    })

    await Promise.all([taskStore.getTasks(), taskStore.getActiveTaskSession()])

    const currentTaskIndex = taskStore.tableState.data.findIndex((task) => task._id === activeTask.value._id)
    const tasksAfterCurrent = taskStore.tableState.data.slice(currentTaskIndex + 1)
    const pendingTasksAfterCurrent = tasksAfterCurrent.filter((task) => task.status === "pending")

    if (pendingTasksAfterCurrent.length === 0) {
        goToFirstPendingTask()
    }

    if (activeTask.value.status === "completed") {
        goToNextTask()
    }
}

async function completeTask() {
    taskStore.activeTask.status = "completed"
    const completedTask: Partial<Task> = { ...activeTask.value }
    delete completedTask.agent
    delete completedTask._id
    completedTask.agentId = activeTask.value.agent?._id

    await taskStore.updateTask(activeTask.value._id || "", completedTask as Task)

    toastService.addToast({
        type: "success",
        message: "Task successfully completed!",
    })

    if (taskStore.checkIfAllActiveTasksCompleted()) {
        await taskStore.closeTaskSession()
    }
    goToPendingTask()
}

function goToPendingTask() {
    isNextActive.value ? goToNextTask() : goToFirstPendingTask()
}

function goToFirstPendingTask() {
    const firstPendingTask = activeTasks.value.find((task) => task.status === "pending")
    if (firstPendingTask) {
        taskStore.activeTask = firstPendingTask
    }
}

function goBackToPreviousTask() {
    const previousPendingTaskIndex = getPreviousPendingTask()
    if (previousPendingTaskIndex !== -1) {
        taskStore.activeTask = activeTasks.value[previousPendingTaskIndex]
    }
}

function getPreviousPendingTask(): number {
    return activeTasks.value
        .map((task, index) => task.status === "pending" && index < activeTaskIndex.value)
        .lastIndexOf(true)
}

async function goToNextTask() {
    const nextPendingTaskIndex = getNextPendingTask()
    if (nextPendingTaskIndex !== -1) {
        taskStore.activeTask = activeTasks.value[nextPendingTaskIndex]
    }
}

function getNextPendingTask(): number {
    return activeTasks.value.findIndex((task, index) => task.status === "pending" && index > activeTaskIndex.value)
}

onMounted(async () => {
    await taskStore.getActiveTaskSession()

    if (!activeTask.value?._id) {
        await taskStore.closeTaskSession()
    }
})

watch(activeTaskIndex, () => {
    sessionStorage.setItem("savedActiveTaskIndex", activeTaskIndex.value.toString())
})

return (_ctx: any,_cache: any) => {
  const _component_Avatar = _resolveComponent("Avatar")!
  const _component_OverlayPanel = _resolveComponent("OverlayPanel")!

  return (activeTask.value)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createVNode(_component_Avatar, {
            shape: "circle",
            label: fullNameInitiales.value
          }, null, 8, ["label"]),
          _createVNode(GgmsGeneralAgent, {
            agent: activeTask.value.agent,
            class: "!text-base text-white font-medium pr-3 border-r border-white"
          }, null, 8, ["agent"]),
          _createElementVNode("p", _hoisted_3, "Task Title: " + _toDisplayString(activeTask.value.displayName), 1)
        ]),
        _createElementVNode("div", _hoisted_4, [
          _createVNode(GgmsButton, {
            styleType: "primary",
            iconStart: _unref(CalendarIcon),
            onClick: toggleReschedulePanel
          }, {
            default: _withCtx(() => [
              _createTextVNode(" Reschedule ")
            ]),
            _: 1
          }, 8, ["iconStart"]),
          _createVNode(_component_OverlayPanel, {
            ref_key: "rescheduleOverlayPanel",
            ref: rescheduleOverlayPanel
          }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_5, [
                _hoisted_6,
                _createElementVNode("p", _hoisted_7, _toDisplayString(activeTaskDate.value), 1),
                _hoisted_8,
                _createElementVNode("div", _hoisted_9, [
                  _createVNode(GgmsInput, {
                    modelValue: newTaskDate.value,
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((newTaskDate).value = $event)),
                    class: "w-[150px]",
                    type: "date",
                    small: true,
                    iconStart: _unref(CalendarIcon)
                  }, null, 8, ["modelValue", "iconStart"]),
                  _createVNode(GgmsInput, {
                    modelValue: newTaskTime.value,
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((newTaskTime).value = $event)),
                    class: "w-[132px]",
                    type: "time",
                    small: true,
                    iconStart: _unref(ClockIcon)
                  }, null, 8, ["modelValue", "iconStart"])
                ]),
                _createElementVNode("div", _hoisted_10, [
                  _createVNode(GgmsButton, {
                    styleType: "simple",
                    classes: ['py-0 font-normal px-0'],
                    onClick: rescheduleTask
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode("Save")
                    ]),
                    _: 1
                  }),
                  _createVNode(GgmsButton, {
                    styleType: "simple",
                    classes: ['text-gray-700 font-normal py-0 px-0'],
                    onClick: toggleReschedulePanel
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode("Cancel")
                    ]),
                    _: 1
                  })
                ])
              ])
            ]),
            _: 1
          }, 512),
          (isPreviousActive.value)
            ? (_openBlock(), _createBlock(GgmsButton, {
                key: 0,
                styleType: "primary",
                iconStart: _unref(RewindIcon),
                onClick: goBackToPreviousTask
              }, {
                default: _withCtx(() => [
                  _createTextVNode("Previous ")
                ]),
                _: 1
              }, 8, ["iconStart"]))
            : _createCommentVNode("", true),
          (isNextActive.value)
            ? (_openBlock(), _createBlock(GgmsButton, {
                key: 1,
                styleType: "primary",
                iconStart: _unref(FastForwardIcon),
                onClick: goToNextTask
              }, {
                default: _withCtx(() => [
                  _createTextVNode("Next")
                ]),
                _: 1
              }, 8, ["iconStart"]))
            : _createCommentVNode("", true),
          _createVNode(GgmsButton, {
            styleType: "primary",
            iconStart: _unref(CheckCircleIcon),
            onClick: completeTask
          }, {
            default: _withCtx(() => [
              _createTextVNode("Complete")
            ]),
            _: 1
          }, 8, ["iconStart"]),
          _createElementVNode("div", _hoisted_11, [
            _hoisted_12,
            _createElementVNode("p", _hoisted_13, _toDisplayString(activeTaskIndex.value + 1) + "/" + _toDisplayString(activeTasks.value.length), 1)
          ]),
          _createElementVNode("button", {
            class: "bg-primary-color-600 p-2",
            onClick: _cache[2] || (_cache[2] = 
//@ts-ignore
(...args) => (_unref(taskStore).closeTaskSession && _unref(taskStore).closeTaskSession(...args)))
          }, [
            _createVNode(_unref(XIcon), { class: "w-6 h-6 stroke-white" })
          ])
        ])
      ]))
    : _createCommentVNode("", true)
}
}

})